/*
 * <modal-box> — KiT 디자인 시스템의 모달 (snippets/modal-box.liquid).
 *
 * ⚠️ 이름이 비슷한 <modal-dialog>(Dawn 기본, 이 파일 아래쪽에서 언급)와 다른 컴포넌트입니다.
 *    <modal-box>    = KiT 디자인 시스템의 모달 (Figma "modalDialog"). 새 모달은 전부 이것.
 *    <modal-dialog> = Dawn이 상품 미디어 라이트박스에 쓰는 껍데기 (assets/script-global.js).
 *
 * Built on the native <dialog> + showModal() rather than on Dawn's <modal-dialog>
 * (assets/script-global.js), because showModal() promotes the dialog into the browser's top
 * layer: it escapes every ancestor's z-index, overflow and transform. That is exactly the trap
 * the bottom sheet had to be designed around (component-dropdown-menu.css), and it matters here
 * because callers render the dialog wherever it belongs semantically — inside a PDP card, say —
 * rather than having to move it to <body> first the way ModalDialog does. Focus trapping,
 * Escape-to-dismiss, inertness of the page behind and the ::backdrop all come from the browser
 * for free too.
 *
 * What is left for this element:
 *   - an open/close API (show/hide) plus a delegated [data-modal-open="<id>"] opener
 *   - the body scroll lock while open (theme convention: body.overflow-hidden) — showModal()
 *     makes the page inert but does not stop it scrolling
 *   - click-the-backdrop to dismiss
 *   - handing focus back to whatever opened the dialog
 */
if (!customElements.get('modal-box')) {
  customElements.define(
    'modal-box',
    class ModalBox extends HTMLElement {
      // Dialogs currently holding the body scroll lock, so two of them can't fight over it.
      static locks = new Set();

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) return;

        this.onClick = this.onClick.bind(this);
        this.onClose = this.onClose.bind(this);

        this.dialog.addEventListener('click', this.onClick);
        this.dialog.addEventListener('close', this.onClose);
      }

      disconnectedCallback() {
        this.dialog?.removeEventListener('click', this.onClick);
        this.dialog?.removeEventListener('close', this.onClose);
        this.releaseLock();
      }

      /* ---- Open / close ------------------------------------------------------ */

      show(opener) {
        if (!this.dialog || this.dialog.open) return;
        // Remembered rather than read back on close: by then focus has already moved into the
        // dialog, so document.activeElement no longer points at the opener.
        this.openedBy = opener || document.activeElement;
        this.dialog.showModal();
        this.takeLock();
      }

      hide() {
        if (!this.dialog || !this.dialog.open) return;
        this.dialog.close(); // fires 'close' -> onClose()
      }

      onClick(event) {
        if (event.target.closest('[data-modal-close]')) {
          this.hide();
          return;
        }
        // A click on the ::backdrop reports the <dialog> itself as its target. The dialog carries
        // no padding of its own (component-modal-box.css puts all of it on the panel inside), so
        // nothing but the backdrop can produce that target.
        if (this.dataset.staticBackdrop === undefined && event.target === this.dialog) this.hide();
      }

      // Fires for every close: a [data-modal-close] button, Escape (native), the backdrop, or
      // a direct dialog.close() — so the lock and the focus hand-back live here and nowhere else.
      onClose() {
        this.releaseLock();
        if (this.openedBy && typeof this.openedBy.focus === 'function') {
          this.openedBy.focus({ preventScroll: true });
        }
        this.openedBy = null;
      }

      /* ---- Body scroll lock -------------------------------------------------- */

      takeLock() {
        ModalBox.locks.add(this);
        document.body.classList.add('overflow-hidden');
      }

      releaseLock() {
        if (!ModalBox.locks.delete(this)) return;
        if (ModalBox.locks.size === 0) document.body.classList.remove('overflow-hidden');
      }
    }
  );

  // Delegated opener, so a dialog can be raised from markup alone. Inside the customElements
  // guard because the snippet emits one <script> per render and this must bind exactly once.
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (!opener) return;
    const box = document.getElementById(opener.dataset.modalOpen);
    if (!box || typeof box.show !== 'function') return;
    event.preventDefault();
    box.show(opener);
  });
}
